/**
 * Session Recovery DB
 *
 * Holds the in-flight session state across page navigations and tab kills.
 * The row is created when recording starts, updated to "processing" when the
 * user taps End & Generate, and finally deleted ONLY after ReviewNotePage
 * successfully submits.
 *
 * Status values:
 *   recording  — ActiveSessionPage is recording
 *   paused     — ActiveSessionPage is paused
 *   processing — ProcessingPage is generating a note
 *   review     — ReviewNotePage has the note open
 *   draft      — note saved as draft (kept for diagnostic only)
 */

const RECOVERY_DB = "FieldNotesRecovery";
const RECOVERY_STORE = "sessions";

function openRecoveryDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(RECOVERY_DB, 1);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(RECOVERY_STORE)) {
        db.createObjectStore(RECOVERY_STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveRecoveryData(data) {
  try {
    const db = await openRecoveryDB();
    const tx = db.transaction(RECOVERY_STORE, "readwrite");
    tx.objectStore(RECOVERY_STORE).put(data);
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch (e) {
    console.error("[Recovery] save failed:", e);
    return false;
  }
}

export async function getRecoveryData() {
  try {
    const db = await openRecoveryDB();
    const tx = db.transaction(RECOVERY_STORE, "readonly");
    return new Promise((resolve) => {
      const req = tx.objectStore(RECOVERY_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    });
  } catch (e) {
    return [];
  }
}

export async function getRecoveryById(id) {
  try {
    const db = await openRecoveryDB();
    const tx = db.transaction(RECOVERY_STORE, "readonly");
    return new Promise((resolve) => {
      const req = tx.objectStore(RECOVERY_STORE).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
}

export async function clearRecoveryData(id) {
  try {
    const db = await openRecoveryDB();
    const tx = db.transaction(RECOVERY_STORE, "readwrite");
    tx.objectStore(RECOVERY_STORE).delete(id);
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch (e) {
    console.error("[Recovery] clear failed:", e);
    return false;
  }
}

/**
 * Clear the localStorage transcript backup for a session.
 * Use only after the note has been safely submitted/persisted.
 */
export function clearLocalStorageBackup(sessionId) {
  try {
    localStorage.removeItem(`fn_session_${sessionId}`);
  } catch (e) {
    /* ignore */
  }
}
